/* ============================================================
   tc60-v6 · COUNT-UP NUMBERS

   Any element with `data-countup` animates from 0 to its final value the
   first time it scrolls into view. Opt-in per element, because a page that
   counts up EVERY number is a slot machine.

   WHAT IS WIRED, and it is deliberately a short list — the page's CLAIMS:
     · the hero's 60         the biggest number in any header here, and the
                             subject of the whole page
     · 5,296 in the People headline, and the 5.0 beside it
     · the three lifetime stats
   Six elements. They are the numbers a reader would otherwise skim.

   WHAT IS DELIBERATELY NOT WIRED:
     · the collage wall's statement tiles (60 Years / 12 Brand principals /
       1966). They are not headers, they sit inside 24 tiles that are already
       sliding horizontally on scroll, and counting inside moving boxes is
       motion on top of motion. They are also CLONED by fillRows() to cover
       the viewport, so one number would count several times over on one
       screen at different moments.
     · 1966 anywhere. A year is not a quantity; counting up to it from zero
       reads as a bug, and the hero's flight instrument already runs
       1966 → 2026 on scroll, which is the honest version of that idea.
     · the calculator's running total. It already animates, from the user's
       own clicks, and that number means something.
     · the awards ring's plaques. They rotate in 3D and are mostly not facing
       the reader.

   THREE THINGS THIS HAS TO GET RIGHT, all of them boring:

   1 · NO LAYOUT SHIFT. "0" is three glyphs narrower than "480,000", so a
       naive counter makes the whole line reflow on every frame and the
       label beside it jitters. Two defences, both needed:
         · `font-variant-numeric: tabular-nums` (CSS) so every digit has the
           same advance width — otherwise 1s are narrower than 8s and the
           number twitches even at a fixed digit count.
         · the element's final width is measured BEFORE the first frame and
           pinned as a min-width, so the box is already its end size while
           the digits are still short.
       Applied in the CSS as `.cup`; this file measures and pins.

   2 · IT MUST BE READ AS THE FINAL VALUE. The markup holds the real number
       as text, so a reader with JS off, a crawler, or a screen reader gets
       "5,296" and never sees a counter. We snapshot that text, animate over
       the top of it, and put it back verbatim at the end — including the
       "+" on any value that has one. `aria-hidden` is NOT used; instead the
       element is marked `aria-live="off"` equivalent by simply never being
       a live region, so AT reads it once when reached.

   3 · REDUCED MOTION GETS NOTHING. Not a faster count — no count. The
       number is already correct in the DOM, so the branch is a no-op.

   Easing is an ease-out cubic: a count-up wants to arrive, not to coast.
   Duration scales gently with magnitude so 5.0 doesn't take as long as
   1,150,000, but it's capped — nobody watches a number for two seconds.
   ============================================================ */
(function () {
  'use strict';

  var nodes = Array.prototype.slice.call(document.querySelectorAll('[data-countup]'));
  if (!nodes.length) return;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;

  /* Split the authored text into prefix / number / suffix so "5,000+" keeps
     its plus and "$1,299" would keep its dollar sign. We animate the middle
     and reprint the outside every frame. */
  function parts(el) {
    var raw = el.textContent.trim();
    var m = raw.match(/^([^\d]*)([\d.,]+)(.*)$/);
    if (!m) return null;
    return { pre: m[1], suf: m[3], raw: raw };
  }

  function format(v, dp) {
    var s = dp ? v.toFixed(dp) : String(Math.round(v));
    if (dp) {
      var half = s.split('.');
      return half[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + half[1];
    }
    return s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ease-out cubic */
  function ease(t) { return 1 - Math.pow(1 - t, 3); }

  function run(el) {
    var target = parseFloat(el.getAttribute('data-countup'));
    if (isNaN(target)) return;

    var dp = parseInt(el.getAttribute('data-dp') || '0', 10);
    var p  = parts(el);
    if (!p) return;

    /* Pin the width at its FINAL size before the first short frame is
       painted — see note 1. Measured off the live element while it still
       holds the final text, which is the only moment that measurement is
       free and correct. */
    var w = el.getBoundingClientRect().width;
    if (w) el.style.minWidth = Math.ceil(w) + 'px';

    /* 900ms for a small number, 1600ms for a seven-figure one. */
    var digits = Math.max(1, String(Math.round(target)).length);
    var dur = Math.min(1600, 640 + digits * 130);

    /* Start from a value, not from zero, when the number is a rating: a
       5.0 star score rolling up from 0.0 reads as a bad review for 400ms.
       Anything under 10 with decimals starts at 60% of target. */
    var from = (dp && target < 10) ? target * 0.6 : 0;

    var t0 = null;
    function frame(ts) {
      if (t0 === null) t0 = ts;
      var t = Math.min(1, (ts - t0) / dur);
      el.textContent = p.pre + format(from + (target - from) * ease(t), dp) + p.suf;
      if (t < 1) requestAnimationFrame(frame);
      /* Reprint the authored string verbatim. Our formatter agrees with it
         today, but the authored text is the source of truth and a future
         "5,296" written as "5.3k" must survive this. */
      else el.textContent = p.raw;
    }
    requestAnimationFrame(frame);
  }

  if (!('IntersectionObserver' in window)) return;   /* number already correct */

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      run(e.target);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.1 });

  /* Wait for webfonts. A count-up measured in the fallback face pins a
     min-width from the wrong metrics, and the number then sits in a box
     that is too wide or too narrow for the rest of its life. */
  function start() { nodes.forEach(function (el) { io.observe(el); }); }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(start);
  else start();
})();
